import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useCampaigns } from "../../hooks/collectionManager/useCampaigns";
import { AuthContext } from "../../cognito/AuthProvider";
import Nav from "../../hooks/Nav";

import Table from "../../tailwindUI/Table";
import Pagination from "../../tailwindUI/Pagination";
import EmptyState from "../../tailwindUI/EmptyState";
import PrimaryButton from "../../tailwindUI/PrimaryButton";

import {
  AdjustmentsHorizontalIcon,
  EyeIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import SearchBar from "../../tailwindUI/SearchBar";
import Sort from "../../tailwindUI/Sort";
import Filters from "../../tailwindUI/Filters";
import { useFilters } from "../../hooks/useFilters";
import { useSorts } from "../../hooks/useSorts";
import SlideOver from "../../tailwindUI/SlideOver";
import { UpdateNotificationsDate } from "./UpdateNotificaionsDate";

const tableColumns = [
  { heading: "ID", value: "campaign_id", main: true },
  { heading: "Fecha", value: "created" },
  { heading: "Links de pago", value: "payment_links_status", align: "center" },
  { heading: "Notificaciones", value: "notifications_status", align: "center" },
  { heading: "Estatus", value: "status", align: "center" },
];

const filterValues = [
  {
    id: 1,
    name: "Estatus",
    value: "status",
    open: false,
    options: [
      {
        id: 1,
        value: "active",
        label: "Activo",
        applied: false,
        checked: false,
        filter_id: 1,
      },
      {
        id: 2,
        value: "pending",
        label: "Pendiente",
        applied: false,
        checked: false,
        filter_id: 1,
      },
      {
        id: 3,
        value: "finished",
        label: "Terminado",
        applied: false,
        checked: false,
        filter_id: 1,
      },
    ],
  },
];

const sortValues = [
  {
    id: 1,
    name: "Los más nuevos",
    field: "created",
    sort: "reverse",
    active: true,
  },
  {
    id: 2,
    name: "Los más antiguos",
    field: "created",
    sort: "direct",
    active: false,
  },
];

export function Campaings() {
  const { user } = React.useContext(AuthContext);
  const history = useHistory();

  const [openSlide, setOpenSlide] = useState(false);
  const { sortOptions, setSortOptions, sortString } = useSorts(sortValues);

  const {
    filters,
    setFilters,
    setFiltersApplied,
    filterString,
    handleCleanFilters,
  } = useFilters(filterValues);

  const {
    campaignsData,
    sendManualNotifications,
    isLoading,
    pagination,
    currentPage,
    setCurrentPage,
    search,
    setSearch,
  } = useCampaigns({ filterString, sortString });

  const columnActions = [
    {
      id: 1,
      name: "Visualizar",
      type: "primary",
      icon: <EyeIcon className="w-5 h-5 text-gray-600 lg:text-white" />,
      action: (campaign) =>
        history.push(`collecting-manager/view/${campaign.campaign_id}`),
    },
  ];

  return (
    <div className="w-full h-full pb-8">
      <div className="w-full xs:h-full md:h-full">
        <div className="w-full bg-white xs:h-auto md:h-full">
          <Nav user={user}>
            <div className="w-full">
              <span className="text-3xl text-white font-bold">Campañas</span>
            </div>
          </Nav>
        </div>
        <div className="w-full px-4 py-3 pt-20 lg:pt-3">
          <div className="flex justify-end lg:justify-between items-center w-full">
            <h1 className="hidden lg:block text-4xl font-bold xs:text-center md:text-left">
              Campañas
            </h1>
            <div className="w-full flex flex-col lg:flex-row gap-6 lg:items-center justify-end">
              <div className="w-full lg:w-fit">
                <PrimaryButton
                  isFullWidth={true}
                  onClick={() =>
                    history.push("/collecting-manager/create-campaign")
                  }
                >
                  <span className="text-[15px] md:text-sm py-0.5 md:py-1 font-medium">
                    Crear campaña
                  </span>
                  <PlusIcon className="w-5 h-5 font-bold ml-2" />
                </PrimaryButton>
              </div>
              <div className="w-full lg:w-fit">
                <PrimaryButton
                  isFullWidth={true}
                  onClick={sendManualNotifications}
                >
                  <span className="text-[15px] md:text-sm py-0.5 md:py-1 font-medium">
                    Enviar notificaciones
                  </span>
                </PrimaryButton>
              </div>
              <UpdateNotificationsDate />
            </div>
          </div>
        </div>

        <div className="w-full px-4 flex flex-col gap-4 pt-4">
          <SearchBar
            value={search}
            setValue={setSearch}
            placeholder_desktop="Buscar por ID"
          />
          <div className="w-full flex justify-between gap-4">
            <Sort
              options={sortOptions}
              title="Ordenar"
              setOptions={setSortOptions}
            />
            <div className="flex items-center gap-6">
              {filterString !== "" && (
                <span
                  className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 flex gap-1 pt-0 md:pt-[1px]"
                  onClick={handleCleanFilters}
                >
                  Limpiar filtros <TrashIcon className="h-5 w-5" />
                </span>
              )}
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-medium text-gray-700 md:hidden"
                onClick={() => setOpenSlide(true)}
              >
                Filtros{" "}
                <span>
                  <AdjustmentsHorizontalIcon className="w-[18px] h-[18px]" />
                </span>
              </button>
              <Filters
                filters={filters}
                setFilters={setFilters}
                setFiltersApplied={setFiltersApplied}
              />
            </div>
          </div>
        </div>

        <div className="w-full p-4 mb-12 lg:mb-4">
          {isLoading ? (
            <Table
              title="Campañas"
              data={campaignsData}
              isLoadingData={isLoading}
              columns={tableColumns}
              actions={columnActions}
            />
          ) : (
            <>
              {campaignsData.length > 0 ? (
                <div className="space-y-4">
                  <Table
                    title="Campañas"
                    data={campaignsData}
                    isLoadingData={isLoading}
                    columns={tableColumns}
                    actions={columnActions}
                  />
                  <Pagination
                    pages={pagination?.totalPages}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                  />
                </div>
              ) : (
                <div className="w-full">
                  <EmptyState
                    title="No se encontraron campañas"
                    text="Verifica la información antes de continuar."
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <SlideOver open={openSlide} setOpen={setOpenSlide} title="Filtros">
        <Filters
          filters={filters}
          openSlide={openSlide}
          setFilters={setFilters}
          setOpenSlide={setOpenSlide}
          setFiltersApplied={setFiltersApplied}
        />
      </SlideOver>
    </div>
  );
}
